import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, HeadingText } from '@paypalcorp/pp-react';
import { Message } from '@paypalcorp/worldready-react';
import PropTypes from 'prop-types';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import ContactOption from '../../../components/ContactOption/ContactOption';
import {
  chatbotDefaultConfig,
  contactOptionIcons,
  contactOptionsFptiPages,
  contactOptionType,
  pageNames,
} from '../../../common/constants';
import { generateLinkData, trackActivityEvent } from '../../../helpers/analytics/fptiHelper';
import LoaderOverlay from '../../../shared/LoaderOverlay';
import useLoaderHook from '../../../shared/hooks/useLoaderHook';
import { chatBotShowAfterSpinner } from '../../../components/Chatbot/ChatbotAction';
import { channelsFilterForCommunity, joinContactOptions } from '../../../helpers/twHelper';
import { FPTI_DATA } from '../../../common/propTypes';
import {
  CONTACT_ARTICLE,
  CONTACT_CALL_US,
  CONTACT_CHANNEL_OPTIONS,
  CONTACT_TOPIC_TREE_INTENTS,
  LOCATION,
  OCP_CONTEXT_RESPONSE,
  POST_CHAT_BOT_ELIGIBILITY,
} from '../../../common/storeConstants';
import { isAsyncUser } from '../../../../js/lib/asyncMessageHelper';

const panelType = {
  OTHER_OPTIONS: 'other',
  RECOMMENDED: 'recommended',
};
const enabledChannels = Object.values(contactOptionIcons).slice();

const ContactOptionsPanel = function (props) {
  const { channels, isIDILoading } = useSelector((state) => state[CONTACT_CHANNEL_OPTIONS]);
  const { isLoading: isLoadingCallus } = useSelector((state) => state[CONTACT_CALL_US]);
  const { article } = useSelector((state) => state[CONTACT_ARTICLE]);
  const { chatPillOcpParams } = useSelector((state) => state[OCP_CONTEXT_RESPONSE]);
  const { chatBotAsync } = useSelector((state) => state[POST_CHAT_BOT_ELIGIBILITY]);
  const { pageName: page } = useSelector((state) => state[LOCATION]);
  const {
    selectedFinalIntent,
    isLoading: isLoadingPriorityIntents,
    searchSheetOpen,
    isOcpLoading,
  } = useSelector((state) => state[CONTACT_TOPIC_TREE_INTENTS]);
  const { callUsInfo } = useSelector((state) => state[CONTACT_CALL_US]);

  // chatbot states
  const [link, setLink] = useState(chatbotDefaultConfig.LINK);
  const [chatBotTopicRefId, setChatBotTopicRefId] = useState(chatbotDefaultConfig.chatBotTopicRefId);
  const [chatPillOcpParamsValue, setChatPillOcpParamsValue] = useState(null);

  // channels state
  const [channelsAvailable, setChannelsAvailable] = useState([]);

  const dispatch = useDispatch();
  const { FPTIData, pageName } = props;

  const { articleId } = { ...article };
  const { ref_id: topicRefId, topic_name: topicName } = { ...selectedFinalIntent };

  const { enableSpinner } = useLoaderHook(() => {
    dispatch(
      chatBotShowAfterSpinner({
        chatBotShow: !isLoadingCallus && !isIDILoading && !isLoadingPriorityIntents && !isOcpLoading,
      }),
    );
  }, [isLoadingCallus, isIDILoading, isLoadingPriorityIntents, isOcpLoading]);

  const handleChannelsFPTIEvent = (contactOptions) => {
    // when IDI channels is not loading and search sheet is open and callUs page is not open and priority intents are available  then fire channels FPTI event
    if (!isIDILoading && !searchSheetOpen && _isEmpty(callUsInfo) && !isLoadingPriorityIntents) {
      // if channels are available
      // join all the channels, parse into string and then show them in the fpti event
      let parsedChannels = joinContactOptions(contactOptions);
      // concatenate articleId into fpti channels
      if (articleId) parsedChannels = parsedChannels.concat(`|${articleId}`);

      let fptiData = {
        ...FPTIData.contactOptionsShown,
        page: generateLinkData(FPTIData.contactOptionsShown.page, { type: contactOptionType.DEFAULT }),
        link: generateLinkData(FPTIData.contactOptionsShown.link, {
          type: contactOptionType.DEFAULT,
          parsedChannels,
        }),
        event_name: generateLinkData(FPTIData.contactOptionsShown.event_name, { type: contactOptionType.DEFAULT }),
      };

      if (topicRefId) {
        fptiData = {
          ...FPTIData.contactOptionsShown,
          page: generateLinkData(FPTIData.contactOptionsShown.page, { type: contactOptionsFptiPages.FINAL_INTENT }),
          link: generateLinkData(FPTIData.contactOptionsShown.link, {
            type: contactOptionsFptiPages.FINAL_INTENT,
            intentCode: topicRefId,
            parsedChannels,
          }),
          event_name: generateLinkData(FPTIData.contactOptionsShown.event_name, {
            type: contactOptionType.FINAL_INTENT,
          }),
        };
      }

      trackActivityEvent(fptiData);
    }
  };

  const checkChannelsForFPTIEvents = (contactOptions, contactOptionsChanged) => {
    // page has to be existed
    // trigger channels FPTI events when contact channels are changed, or final intent selected, or (page is contact us page and callUs is not available)
    if (page && (contactOptionsChanged || topicRefId || page === pageNames.CONTACT_US))
      return handleChannelsFPTIEvent(contactOptions);
    return null;
  };

  useEffect(() => {
    if (Array.isArray(channels) && channels.length) {
      // sort the channels on priority
      const contactOptions = channels
        .filter((channel) => enabledChannels.includes(channel.type) && channelsFilterForCommunity(channel.type))
        .sort((a, b) => Number(a.priority) - Number(b.priority));

      if (articleId) contactOptions.push({ type: contactOptionIcons.ARTICLE });

      // only update the channelsAvailable when the contact channels are changed
      setChannelsAvailable((prevContactOptions) => {
        const isEqual = _isEqual(prevContactOptions, contactOptions);
        // fire contact option event when search sheet is not open, when the idi channels are available, and when contact channels are changed or final intent is changed or call us page closed
        checkChannelsForFPTIEvents(contactOptions, !isEqual);
        if (!isEqual) return contactOptions;
        return prevContactOptions;
      });
    }
  }, [channels, isIDILoading, searchSheetOpen, articleId, callUsInfo, page, isLoadingPriorityIntents]);

  // manage the change of topicRefId and chatPillOcpParams
  // update chatBotTopicRefId, chatPillOcpParams & link and they all are for initiate chatBot window
  useEffect(() => {
    if (topicRefId) {
      const finalIntentTopicRefId = `${topicRefId}|finalIntent`;
      setChatBotTopicRefId(finalIntentTopicRefId);
      setLink(`${link}|final|${finalIntentTopicRefId}`);
      if (chatPillOcpParams) setChatPillOcpParamsValue(chatPillOcpParams);
    }
  }, [topicRefId, chatPillOcpParams]);

  const renderContactOption = (panel) => {
    if (!channelsAvailable || !channelsAvailable.length) {
      return null;
    }

    const chatBotConfig = {
      selectedFinalIntent,
      chatBotTopicRefId,
      topicName,
      link,
      isChatBotAsyncUser: isAsyncUser(chatBotAsync),
      chatPillOcpParams: chatPillOcpParamsValue,
    };

    switch (panel) {
      // channelsAvailable is already sorted based on the priority so the first one should recommended, then the rest should be other contact options
      case panelType.RECOMMENDED: {
        const recommendedContactChannel = [...channelsAvailable].slice(0, 1);
        return recommendedContactChannel.map((channel) => {
          return <ContactOption type={channel.type} FPTIData={FPTIData} {...chatBotConfig} />;
        });
      }
      case panelType.OTHER_OPTIONS: {
        const otherContactChannels = [...channelsAvailable].slice(1);
        return otherContactChannels.map((channel) => {
          return <ContactOption type={channel.type} article={article} FPTIData={FPTIData} {...chatBotConfig} />;
        });
      }
      default:
        return null;
    }
  };

  const renderContactOptionPanel = (panel) => (
    <Container className={`sh_${panel}_option`} data-testid={`sh_${panel}_option`}>
      <HeadingText className="sh_contact_options__heading_text" size="sm" data-testid="sh-contact-options-heading-text">
        <Message id={`smarthelp.app.contactOption.heading.${panel}`} />
      </HeadingText>
      {renderContactOption(panel)}
    </Container>
  );

  // if page is not contactUs
  if (page !== pageName) {
    return null;
  }

  return (
    <div className="sh_contact_options" data-testid="sh-contact-options">
      <LoaderOverlay display={enableSpinner} />

      {/* Render contact-options headers:
              - RECOMMENDED: show when we have at least 1 channel available as 1st channel would have priority 1
              - OTHER_OPTIONS: show when we have at least 2 channels available
        */}
      {Array.isArray(channelsAvailable) && channelsAvailable.length > 0 && (
        <Fragment>
          <hr />
          {renderContactOptionPanel(panelType.RECOMMENDED)}
        </Fragment>
      )}
      {Array.isArray(channelsAvailable) && channelsAvailable.length > 1 && (
        <Fragment>
          <hr />
          {renderContactOptionPanel(panelType.OTHER_OPTIONS)}
        </Fragment>
      )}
    </div>
  );
};

ContactOptionsPanel.propTypes = {
  FPTIData: PropTypes.shape(FPTI_DATA).isRequired,
  pageName: PropTypes.string.isRequired,
};

export default ContactOptionsPanel;
